import React, { useState } from 'react'
import { FileText } from 'lucide-react'
import { Modal, Select } from 'antd'
import { TYPOGRAPHY } from '../../../styles/theme'

const CONTENT_TYPE_OPTIONS = [
  { value: 'General research', label: 'General research' },
  { value: 'Interview transcripts', label: 'Interview transcripts' },
  { value: 'Survey data', label: 'Survey data' },
  { value: 'Product description', label: 'Product description' },
  { value: 'Business plan', label: 'Business plan' },
  { value: 'User feedback', label: 'User feedback' },
  { value: 'Other', label: 'Other' },
]

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface DocumentClassificationModalProps {
  files: File[]
  onConfirm: (classification: string) => void
  onCancel: () => void
}

export const DocumentClassificationModal = React.memo(
  ({ files, onConfirm, onCancel }: DocumentClassificationModalProps) => {
    const [classification, setClassification] = useState('General research')

    const handleOk = () => {
      onConfirm(classification)
    }

    return (
      <Modal
        open={files.length > 0}
        title="Classify uploaded file"
        okText="Upload"
        cancelText="Cancel"
        onOk={handleOk}
        onCancel={onCancel}
        width={400}
        destroyOnHidden
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {files.map((file) => (
              <div
                key={file.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 10px',
                  borderRadius: 6,
                  background: 'rgba(59, 130, 246, 0.06)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                }}
              >
                <FileText size={14} style={{ flexShrink: 0, color: '#3b82f6', opacity: 0.8 }} />
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: TYPOGRAPHY.caption.fontSize,
                  }}
                >
                  {file.name}
                </span>
                <span
                  style={{
                    fontSize: TYPOGRAPHY.caption.fontSize,
                    color: 'var(--color-text-muted)',
                    flexShrink: 0,
                  }}
                >
                  {formatFileSize(file.size)}
                </span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span
              style={{
                fontSize: TYPOGRAPHY.caption.fontSize,
                color: 'var(--color-text-secondary)',
                fontWeight: 600,
              }}
            >
              What type of content is this?
            </span>
            <Select
              value={classification}
              onChange={setClassification}
              options={CONTENT_TYPE_OPTIONS}
              style={{ width: '100%' }}
              getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
            />
          </div>
        </div>
      </Modal>
    )
  }
)

DocumentClassificationModal.displayName = 'DocumentClassificationModal'
